import React, { memo, useCallback, useMemo } from 'react';
import { YStack } from 'tamagui';
import CobuText from './CobuText';
import { getAvatarColor } from '../../utils/avatarColors';

interface SimpleAvatarProps {
    user: {
        uuid: string;
        displayName: string;
    };
    fontSize?: number;
    size?: number;
}

/**
 * SimpleAvatar Component
 * 
 * A fallback avatar component that displays user initials with a dynamic background color
 * based on the user's UUID for consistency across the app.
 * 
 * Features:
 * - Generates initials from display name
 * - Consistent color assignment based on user UUID
 * - Customizable font size and style
 * - Optimized with React.memo
 */
const CobuSimpleAvatar: React.FC<SimpleAvatarProps> = memo(({
    user,
    fontSize = 17,
    size = 40
}) => {
    const getInitials = useCallback((displayName: string) => {
        if (!displayName) return 'U';
        const names = displayName.trim().split(' ');
        if (names.length === 1) return names[0][0].toUpperCase();
        return `${names[0][0]}${names[names.length - 1][0]}`.toUpperCase();
    }, []);

    const avatarColor = useMemo(() => getAvatarColor(user.uuid), [user.uuid]);

    return (
        <YStack
            w={size}
            h={size}
            br={size / 2}
            style={{ backgroundColor: avatarColor.backgroundColor }}
            ai="center"
            jc="center"
        >
            <CobuText
                variant="body"
                color={avatarColor.color}
                fontSize={fontSize}
                fow="$semibold"
            >
                {getInitials(user.displayName)}
            </CobuText>
        </YStack>
    );
});

CobuSimpleAvatar.displayName = 'CobuSimpleAvatar';

export default CobuSimpleAvatar; 